// src/lib/incidents-helpers.js — accesseurs et calculs sur les incidents.
// Pures, pas de dépendance externe. Gèrent la rétrocompat avec l'ancien
// schéma incident.type/nombre/precision (avant le passage en multi-dégradations).
//
// LOT Incidents 13 — les 3 statuts canoniques (en_cours / a_racheter /
// resolu), STATUS_META, normalizeStatus() et statusMeta() vivent dans
// incidents-api.js (couche Supabase). Ce fichier reste PUR (aucune dépendance
// externe) : les helpers KV ci-dessous (getDegs / …) sont encore utilisés par
// le chemin facture (App.jsx, LogementsPanel) et ne sont PAS modifiés.

// Retourne le tableau de dégradations d'un incident, en wrappant l'ancien
// format mono-dégradation si nécessaire (rétrocompat).
export function getDegs(inc) {
  const degs = Array.isArray(inc.degradations) ? inc.degradations.filter(Boolean) : []
  if (degs.length) return degs
  if (inc.type) return [{
    id: inc.id || "leg",
    type: inc.type,
    nombre: inc.nombre || "1",
    precision: inc.precision || inc.description || "",
    statutMenage: inc.statutMenage || "À traiter",
  }]
  return []
}

// Statut "primaire" affiché dans la liste des incidents.
//   - aucune dégradation → fallback sur incident.statutMenage ou "Récupérable"
//   - tous les non-récupérables ont une date de remplacement → "Remplacé"
//   - au moins un non-récupérable non remplacé → "Non récupérable"
//   - ADR-228 : ≥1 dégradation ET toutes "À vérifier" → "À vérifier" (incident
//     pas encore jugé : évite le faux "Récupérable" vert pour un non-jugé)
//   - sinon → "Récupérable"
export function incPrimaryStatut(inc) {
  const degs = getDegs(inc)
  if (!degs.length) return inc.statutMenage || "Récupérable"
  const nonRecup = degs.filter(d => d.statutMenage === "Non récupérable")
  if (nonRecup.length > 0 && nonRecup.every(d => d.remplaceLe)) return "Remplacé"
  if (nonRecup.length > 0) return "Non récupérable"
  if (degs.every(d => d.statutMenage === "À vérifier")) return "À vérifier"
  return "Récupérable"
}

// Total Aircover perçu pour un incident (somme des aircoverMontant de chaque dég).
export const incAircoverTotal = inc =>
  getDegs(inc).reduce((s, d) => s + (parseFloat(d.aircoverMontant) || 0), 0)

// ── 5 états financiers (chantier ⑥) — axe FINANCIER par dégradation ─────────
//
// `etatFinancier` REMPLACE l'ancien champ `traitement` (fbz/info). Ces helpers
// sont PURS (0 I/O) et servent les 3 lecteurs argent : facture proprio
// (facture-html.js), rapport/synthèse (App.jsx) et compta lecture à la volée
// (ComptaTab.jsx). AUCUNE déduction AirCover n'est faite ici : la facture reste
// au BRUT (AirCover perçu par le proprio, hors facture — décision Dimitri).

// Libellés canoniques (dupliqués volontairement de domain-constants pour garder
// ce module SANS dépendance — cf. en-tête). Toute évolution : synchroniser les 2.
const ETAT_SANS_COUT = "Sans coût"
const ETAT_REFACTURE_PROPRIO = "Refacturé propriétaire"
const ETAT_ACHAT_FBZ = "Achat FULLBNBZEN"
const ETAT_REMBOURSE_AIRCOVER = "Remboursé AirCover"
const ETAT_AIRCOVER_REFACTURE = "AirCover + refacturé"

// États qui ALIMENTENT la ligne facture proprio (au BRUT, jamais − AirCover).
const FACTURE_ETATS = new Set([ETAT_REFACTURE_PROPRIO, ETAT_AIRCOVER_REFACTURE])

/**
 * Dérive l'état financier d'une dégradation. Priorité au champ `etatFinancier`
 * (nouvelle forme jsonb). RÉTROCOMPAT : si absent, mappe l'ancien `traitement`
 * (fbz → « Refacturé propriétaire », info → « Remboursé AirCover ») — la table
 * `incidents` V2 est VIDE en prod mais des archives KV peuvent porter `traitement`.
 * Retourne `null` si non qualifié (« À qualifier »).
 * @param {Object} deg
 * @returns {string|null}
 */
export function deriveEtatFinancier(deg) {
  if (!deg) return null
  if (deg.etatFinancier) return deg.etatFinancier
  if (deg.traitement === "fbz") return ETAT_REFACTURE_PROPRIO
  if (deg.traitement === "info") return ETAT_REMBOURSE_AIRCOVER
  return null
}

/** true si la dégradation alimente la facture proprio (refacturé au BRUT). */
export function isFactureEtat(deg) {
  return FACTURE_ETATS.has(deriveEtatFinancier(deg))
}

/** true si la dégradation est un ACHAT FULLBNBZEN (= charge FBZ compta). */
export function isAchatFbz(deg) {
  return deriveEtatFinancier(deg) === ETAT_ACHAT_FBZ
}

/**
 * true si l'incident comporte ≥ 1 dégradation « Non récupérable » (donc porteuse
 * d'un enjeu financier : rachat / refacturation) NON encore QUALIFIÉE financièrement
 * (etatFinancier absent → « À qualifier »). Alimente le badge/indicateur
 * « incidents non qualifiés » côté admin — ne bruite pas les déclarations « À
 * vérifier »/« Récupérable » (sans enjeu argent).
 */
export function needsEtatFinancierQualif(inc) {
  return getDegs(inc).some(d => d && d.statutMenage === "Non récupérable" && deriveEtatFinancier(d) == null)
}

/**
 * Compta LECTURE À LA VOLÉE (chantier ⑥) — charge FBZ des incidents pour un mois.
 * Seul l'état « Achat FULLBNBZEN » est une charge FBZ (le coût est absorbé par
 * FULLBNBZEN). « Refacturé propriétaire » = débours neutre (porté par la facture,
 * jamais compté ici) ; « Remboursé AirCover » / « Sans coût » = hors compta FBZ.
 * L'AirCover perçu par FBZ en sous-loc (`aircoverFbz`) vient EN ATTÉNUATION.
 *
 * Aucune écriture : cette fonction ne fait que LIRE les incidents déjà chargés
 * (jamais de duplication dans `depensesVar`). Gating sur `remplaceLe` dans le mois
 * `mk` (YYYY-MM) — cohérent avec la facture (la charge est réalisée à l'achat).
 *
 * @param {Array} incidents - incidents (shape KV-compat, `logId`/`degradations`)
 * @param {string} mk - clé mois « YYYY-MM »
 * @returns {{ achat: number, aircoverFbz: number, net: number }}
 */
export function computeIncidentComptaFbz(incidents, mk) {
  let achat = 0
  let aircoverFbz = 0
  if (!Array.isArray(incidents) || !mk) return { achat: 0, aircoverFbz: 0, net: 0 }
  for (const inc of incidents) {
    for (const d of getDegs(inc)) {
      if (!isAchatFbz(d) || !d.remplaceLe) continue
      if (String(d.remplaceLe).slice(0, 7) !== mk) continue
      achat += parseFloat(d.cout) || 0
      aircoverFbz += parseFloat(d.aircoverFbz) || 0
    }
  }
  return { achat, aircoverFbz, net: achat - aircoverFbz }
}

// ── LOT Incidents 13 — adaptateur source V2 → shape consommée ──────────
//
// La table V2 `incidents` est désormais la source UNIQUE (admin + ménage).
// Les lecteurs historiques côté admin (dashboard, alerte par-proprio, facture
// proprio, rapport, Annuel) consomment l'ancienne shape KV : `logId`,
// `propId`, `createdAt`, `degradations`, `voyageur`. Cet adaptateur PUR
// convertit une ligne V2 vers cette shape SANS toucher au contenu métier
// (degradations identique entre KV et V2 — cf. migration phase4 cascade,
// "multi-dégradations V1-style"). Aucun montant n'est recalculé ici :
// getDegs / incAircoverTotal / le bloc Aircover de la facture continuent de
// lire `degradations` à l'identique. On expose en plus `status` canonique.
//
// `propByLog` = Map logement_id → propId (construite via getAllLogsFlat dédup
// byId — JAMAIS de lookup par nom). Source manquante → propId null (l'alerte
// par-proprio ignore alors la ligne, comportement défensif).
//
// @param {Object} row - ligne V2 (`incidents`)
// @param {Map<string,string>} [propByLog] - map logement_id → propId
// @returns {Object} incident en shape KV-compat
export function mapV2IncidentToKv(row, propByLog = null) {
  const logId = row?.logement_id ?? null
  return {
    ...row,
    logId,
    propId: propByLog ? propByLog.get(logId) ?? null : null,
    // Les lecteurs filtrent par mois sur `createdAt` (new Date(i.createdAt)).
    // V2 : `created_at` (insert) ; fallback `reported_at` (signalement).
    createdAt: row?.created_at ?? row?.reported_at ?? null,
    // `status` reste la valeur canonique V2 (en_cours/a_racheter/resolu) pour
    // les comparaisons via STATUS.* + normalizeStatus côté lecteurs.
    status: row?.status ?? null,
    degradations: row?.degradations ?? [],
    voyageur: row?.voyageur ?? null,
  }
}

// Mappe un tableau de lignes V2 vers la shape KV-compat. `cfg` sert à résoudre
// propId via getAllLogsFlat (dédup byId). cfg optionnel : sans cfg, propId=null.
export function mapV2IncidentsToKv(rows, propByLog = null) {
  return (rows || []).map(r => mapV2IncidentToKv(r, propByLog))
}
